import { useMemo, useReducer, type ReactNode } from 'react';

import { canRedo, canUndo } from './history';
import { QrContext, type QrActions, type QrContextValue } from './qr-context';
import { createInitialState, qrReducer } from './qr-reducer';

/**
 * Owns the QR design and its undo history.
 *
 * The action object is memoised on `dispatch` (which React guarantees is
 * stable), so it is created once for the lifetime of the provider rather than
 * on every render.
 */
export function QrProvider({ children }: { readonly children: ReactNode }) {
  const [state, dispatch] = useReducer(qrReducer, undefined, () => createInitialState());

  const actions = useMemo<QrActions>(
    () => ({
      setContent: (content) => {
        dispatch({ type: 'setContent', content });
      },
      setErrorCorrectionLevel: (level) => {
        dispatch({ type: 'setErrorCorrectionLevel', level });
      },
      setSize: (size) => {
        dispatch({ type: 'setSize', size });
      },
      setModuleStyle: (style) => {
        dispatch({ type: 'setModuleStyle', style });
      },
      setModuleColor: (color) => {
        dispatch({ type: 'setModuleColor', color });
      },
      setBackgroundColor: (color) => {
        dispatch({ type: 'setBackgroundColor', color });
      },
      setOuterEyeStyle: (style) => {
        dispatch({ type: 'setOuterEyeStyle', style });
      },
      setInnerEyeStyle: (style) => {
        dispatch({ type: 'setInnerEyeStyle', style });
      },
      setEyeColor: (color) => {
        dispatch({ type: 'setEyeColor', color });
      },
      setOuterBorderRadius: (radius) => {
        dispatch({ type: 'setOuterBorderRadius', radius });
      },
      setInnerBorderRadius: (radius) => {
        dispatch({ type: 'setInnerBorderRadius', radius });
      },
      setStyleEyesAsModules: (enabled) => {
        dispatch({ type: 'setStyleEyesAsModules', enabled });
      },
      setCenterImage: (file) => {
        dispatch({ type: 'setCenterImage', file });
      },
      setCenterImageSize: (size) => {
        dispatch({ type: 'setCenterImageSize', size });
      },
      setCustomModuleImage: (file) => {
        dispatch({ type: 'setCustomModuleImage', file });
      },
      reset: () => {
        dispatch({ type: 'reset' });
      },
      undo: () => {
        dispatch({ type: 'undo' });
      },
      redo: () => {
        dispatch({ type: 'redo' });
      },
    }),
    [],
  );

  const value = useMemo<QrContextValue>(
    () => ({
      design: state.present,
      actions,
      canUndo: canUndo(state),
      canRedo: canRedo(state),
    }),
    [state, actions],
  );

  return <QrContext value={value}>{children}</QrContext>;
}
